import { masterDetailsMinHeight, pageBgColor } from "@/utils/theme";
import { ErrorModal, HelmetDexteel } from "@dexteel/mesf-core";
import { Box, CircularProgress, Grid2, Typography } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { MillStatusCard } from "./components/MillStatusCard";
import { SalesOverview } from "./models/SalesOverview";
import { getSalesOverview } from "./repositories/SalesRepository";

const useSalesOverview = () =>
	useQuery<SalesOverview, Error>({
		queryKey: ["salesOverview"],
		queryFn: ({ signal }) => getSalesOverview(signal),
		refetchOnWindowFocus: true,
		refetchInterval: 30_000,
	});

export const SalesPage = () => {
	const [error, setError] = useState("");

	const { data, isLoading, isError, error: queryError } = useSalesOverview();

	useEffect(() => {
		if (isError && queryError) {
			setError(queryError.message);
		}
	}, [isError, queryError]);

	if (isLoading || !data) {
		return (
			<Box
				sx={{
					display: "flex",
					justifyContent: "center",
					alignItems: "center",
					height: "60vh",
				}}
			>
				<CircularProgress />
			</Box>
		);
	}

	const monthName = new Date().toLocaleString("en-US", {
		month: "long",
		year: "numeric",
	});

	return (
		<>
			<HelmetDexteel title="Sales Overview" />
			<Box
				sx={{
					p: 2,
					backgroundColor: pageBgColor,
					minHeight: masterDetailsMinHeight,
				}}
			>
				<Box
					sx={{
						display: "flex",
						justifyContent: "space-between",
						alignItems: "flex-end",
						mb: 2,
						flexWrap: "wrap",
						gap: 1,
					}}
				>
					<Box>
						<Typography
							variant="h5"
							sx={{ fontWeight: 600, userSelect: "none" }}
						>
							Sales Overview
						</Typography>
						<Typography variant="body2" sx={{ color: "#666", mt: 0.5 }}>
							{monthName} · Live production metrics across mills and
							fabrication.
						</Typography>
					</Box>
				</Box>

				<Grid2 container spacing={2}>
					{data.Mills.map((mill) => (
						<Grid2
							key={`${mill.MillId}-${mill.MillName}`}
							size={{ xs: 12, md: 6, lg: 4 }}
						>
							<MillStatusCard
								data={mill}
								events={data.Events.filter((e) => e.MillId === mill.MillId)}
								heatsByEvent={data.HeatsByEvent.filter(
									(h) => h.MillId === mill.MillId,
								)}
							/>
						</Grid2>
					))}
				</Grid2>
			</Box>
			<ErrorModal error={error} onHide={() => setError("")} />
		</>
	);
};
